import { createContext, useContext, useEffect, useState } from 'react';
import * as z from 'zod';

// The primary domains owners authenticate on. Each API lane returns its own.
const PLATFORM_ORIGINS = {
  production: 'https://portal.vrplatform.app',
  staging: 'https://staging.portal.vrplatform.app',
} as const;
type Platform = keyof typeof PLATFORM_ORIGINS;

/**
 * Every hostname that is not a platform, preview or local host is a customer
 * owner portal. Platform hosts are named exactly: other `vrplatform.app`
 * subdomains, such as the edge Worker's own custom domains, may be registered
 * as portals.
 */
export function usesOwnerPortalBootstrap(hostname: string) {
  return (
    !!hostname &&
    hostname.includes('.') &&
    ![
      'localhost',
      '127.0.0.1',
      '[::1]',
      'vrplatform.app',
      'portal.vrplatform.app',
      'finalytic.io',
    ].includes(hostname) &&
    ![
      '.localhost',
      '.portal.vrplatform.app',
      '.finalytic.io',
      '.pages.dev',
      '.workers.dev',
    ].some((suffix) => hostname.endsWith(suffix))
  );
}

export const ownerPortalSchema = z
  .object({
    hostname: z
      .string()
      .regex(
        /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/
      ),
    portalEnabled: z.boolean(),
    teamId: z.uuid(),
    slug: z.string().min(1),
    dataRegion: z.enum(['us', 'eu', 'ap', 'hostaway', 'crunchy']),
    name: z.string().min(1).max(60),
    logo: z.url().nullable(),
    colorPrimary: z.string().regex(/^#[0-9a-f]{6}$/i),
    showPlatformBranding: z.boolean(),
    signInUrl: z.url(),
    signUpUrl: z.url(),
  })
  .refine(
    (portal) =>
      Object.values(PLATFORM_ORIGINS).some(
        (origin) =>
          portal.signInUrl ===
            `${origin}/owner-login?owner_portal=${portal.hostname}` &&
          portal.signUpUrl ===
            `${origin}/owner-sign-up?owner_portal=${portal.hostname}`
      ),
    'Owner portal authentication must use a primary domain'
  );

export type OwnerPortal = z.infer<typeof ownerPortalSchema>;
export const ownerPortalContext = createContext<OwnerPortal | null>(null);
export const useOwnerPortal = () => useContext(ownerPortalContext);

let activeOwnerPortal: OwnerPortal | null = null;

/**
 * The validated registration for the hostname this UI is served from. It is
 * set only after the embedded bootstrap validated for exactly
 * `window.location.hostname`. The Clerk key, API host
 * and diagnostics follow this value, never the hostname alone,
 * so an unrecognised hostname cannot talk to production before validation.
 */
export function setActiveOwnerPortal(portal: OwnerPortal | null) {
  activeOwnerPortal = portal;
}
export function getActiveOwnerPortal() {
  return activeOwnerPortal;
}
export function hasActiveOwnerPortal() {
  return activeOwnerPortal !== null;
}

/**
 * The lane a validated registration belongs to, read from the primary domain
 * its sign-in URL points at. The schema guarantees one of the known origins.
 */
export function getOwnerPortalPlatform(portal: OwnerPortal): Platform {
  return new URL(portal.signInUrl).origin === PLATFORM_ORIGINS.staging
    ? 'staging'
    : 'production';
}
export function getActiveOwnerPortalPlatform() {
  return activeOwnerPortal ? getOwnerPortalPlatform(activeOwnerPortal) : null;
}
export function getActiveOwnerPortalApiHost() {
  return getActiveOwnerPortalPlatform() === 'staging'
    ? 'staging.api.vrplatform.app'
    : 'api.vrplatform.app';
}

/**
 * Reads the edge-injected registration for `hostname`. A registration
 * that is unknown, pending, disabled or answers for another hostname fails.
 * When the registration belongs to the current hostname it becomes the active
 * portal before any caller can mount Clerk or the API client.
 */
export function useOwnerPortalBootstrap(
  hostname: string | undefined,
  enabled: boolean
) {
  const [portal, setPortal] = useState<OwnerPortal | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setPortal(null);
    setFailed(false);
    if (!enabled) return;
    try {
      const script = document.getElementById('vrplatform-owner-portal');
      if (
        script?.tagName !== 'SCRIPT' ||
        script.getAttribute('type') !== 'application/json'
      )
        throw new Error('Owner portal is unavailable');
      const registration = ownerPortalSchema.parse(
        JSON.parse(script.textContent ?? '')
      );
      if (registration.hostname !== hostname || !registration.portalEnabled)
        throw new Error('Owner portal is unavailable');
      if (registration.hostname === window.location.hostname)
        setActiveOwnerPortal(registration);
      setPortal(registration);
      return () => {
        if (getActiveOwnerPortal() === registration) setActiveOwnerPortal(null);
      };
    } catch {
      setFailed(true);
    }
  }, [enabled, hostname]);
  return { portal, failed };
}

export function getOwnerPortalReturnUrl(
  value: string | null,
  hostname: string
) {
  const url = new URL(value ?? `https://${hostname}/`);
  if (url.origin !== `https://${hostname}` || url.username || url.password) {
    throw new Error('Owner portal return address is not allowed');
  }
  return url.href;
}

/** Clerk hash routing and invitation query routing carry the same return contract. */
export function getOwnerPortalRedirectUrl(
  location: Pick<Location, 'search' | 'hash'>,
  hostname: string
) {
  return getOwnerPortalReturnUrl(
    new URLSearchParams(
      location.hash.slice(location.hash.indexOf('?') + 1)
    ).get('redirect_url') ??
      new URLSearchParams(location.search).get('redirect_url'),
    hostname
  );
}

export function canAccessCustomOwnerPortal(args: {
  expectedTeamId: string;
  actualTeamId: string | null | undefined;
  role: string | null | undefined;
}) {
  return args.actualTeamId === args.expectedTeamId && args.role === 'owner';
}

export function getOwnerPortalInvitationUrl(
  currentUrl: string,
  signInUrl: string,
  portal: Pick<OwnerPortal, 'hostname' | 'teamId' | 'signInUrl'>
) {
  const { hostname, teamId } = portal;
  const invitation = new URL(getOwnerPortalReturnUrl(currentUrl, hostname));
  const primary = new URL(signInUrl);
  if (
    invitation.pathname !== '/invitation' ||
    invitation.searchParams.get('tid') !== teamId ||
    invitation.searchParams.get('r') !== 'owner' ||
    primary.origin !== new URL(portal.signInUrl).origin ||
    !!primary.username ||
    !!primary.password
  ) {
    throw new Error('Owner invitation does not belong to this portal');
  }
  // The invitation's own page, such as the bank details form, wins over the
  // portal root the sign-in URL returns to.
  const invitationRedirectUrl = invitation.searchParams.get('redirect_url');
  primary.searchParams.set(
    'redirect_url',
    invitationRedirectUrl
      ? getOwnerPortalReturnUrl(invitationRedirectUrl, hostname)
      : getOwnerPortalRedirectUrl(primary, hostname)
  );
  primary.hash = '';
  primary.pathname = '/invitation';
  for (const key of ['token', 'tid', 'r']) {
    const value = invitation.searchParams.get(key);
    if (!value) throw new Error('Owner invitation is incomplete');
    primary.searchParams.set(key, value);
  }
  primary.searchParams.set('owner_portal', hostname);
  // The owner already accepted on the custom domain's landing page.
  primary.searchParams.set('accept', '1');
  return primary.href;
}
